import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ChevronRight, LoaderCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface WhatsAppInteractiveOption {
  id: string
  title: string
  description?: string
}

export interface WhatsAppListInteractive {
  type: 'list'
  body: string
  action_label: string
  sections: Array<{
    title?: string
    options: WhatsAppInteractiveOption[]
  }>
}

interface Props {
  open: boolean
  interactive: WhatsAppListInteractive
  active: boolean
  submittingOptionId?: string
  onClose: () => void
  onSelect: (option: WhatsAppInteractiveOption) => void
}

export function WhatsAppListPicker({
  open,
  interactive,
  active,
  submittingOptionId,
  onClose,
  onSelect,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return

    previousFocusRef.current = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeButtonRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      previousFocusRef.current?.focus()
    }
  }, [onClose, open])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="whatsapp-list-picker-title"
        className="flex max-h-[85dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-border bg-card text-foreground shadow-2xl sm:max-w-md sm:rounded-2xl"
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-muted-foreground/30 sm:hidden" />
        <header className="grid grid-cols-[2.5rem_1fr_2.5rem] items-center border-b border-border px-3 py-3">
          <button
            ref={closeButtonRef}
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            aria-label="Cerrar lista"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
          <div className="min-w-0 text-center">
            <h2 id="whatsapp-list-picker-title" className="truncate font-semibold">
              {interactive.action_label}
            </h2>
            <p className="text-xs text-muted-foreground">Selecciona una opción</p>
          </div>
          <span aria-hidden="true" />
        </header>

        <div className="overflow-y-auto overscroll-contain">
          {interactive.sections.map((section, sectionIndex) => (
            <section key={`picker-section-${sectionIndex}`} aria-label={section.title}>
              {section.title && (
                <h3 className="bg-muted/50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {section.title}
                </h3>
              )}
              <div>
                {section.options.map((option) => {
                  const isSubmitting = submittingOptionId === option.id
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={cn(
                        'flex min-h-16 w-full items-center gap-3 border-b border-border px-5 py-3 text-left transition-colors last:border-b-0',
                        'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                        'disabled:cursor-not-allowed disabled:opacity-50'
                      )}
                      onClick={() => onSelect(option)}
                      disabled={!active || Boolean(submittingOptionId)}
                      aria-label={`Seleccionar ${option.title}`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{option.title}</span>
                        {option.description && (
                          <span className="mt-0.5 block text-sm text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </span>
                      {isSubmitting ? (
                        <LoaderCircle className="h-5 w-5 shrink-0 animate-spin text-primary" />
                      ) : (
                        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                      )}
                    </button>
                  )
                })}
              </div>
            </section>
          ))}
        </div>

        {!active && (
          <p className="border-t border-border px-5 py-3 text-center text-xs text-muted-foreground">
            Esta lista ya no está activa.
          </p>
        )}
      </div>
    </div>,
    document.body
  )
}
